import {
  TURBINES_KEYS,
  TURBINES_VARIANTS,
  TURBINES_POWER_STATUS
} from '../constants/turbines';
import { getSummaryPath } from './turbinesPaths';

export const getTurbineVariant = turbine => {
  const { isOnline, status, powerStatus, isActive } = turbine;

  if (powerStatus === TURBINES_POWER_STATUS.offline || !isOnline)
    return TURBINES_VARIANTS.offline;

  switch (status) {
    case TURBINES_VARIANTS.shutdown:
      return TURBINES_VARIANTS.shutdown;
    case TURBINES_VARIANTS.warning:
      return TURBINES_VARIANTS.warning;
    case TURBINES_VARIANTS.ok:
      return TURBINES_VARIANTS.ok;
    default:
      return TURBINES_VARIANTS.ok;
  }
};

export const processInstalledProducts = products => {
  if (!Array.isArray(products)) return [];

  const processedProducts = products.map(product => {
    const currentKw = product.genTotalKwAnalog?.value ?? 0;
    const maxKw = product.genTotalKwAnalog?.max ?? 5000;
    const ngpValue = product.measures?.ngpAnalog ?? 0;
    const t5Value = product.measures?.t5AverageTemperatureAnalog ?? 0;
    const loadValue = product.measures?.pcntLoadAnalog ?? 0;

    const variant = getTurbineVariant(product);

    return {
      id: product.id || 'N/A',
      title: product.id || 'N/A',
      subtitle: product.type || 'Unknown',
      mainLabel: product.genTotalKwAnalog?.label || 'Gen Total kW',
      currentKw,
      maxKw,
      metrics: {
        ngp: {
          value: ngpValue,
          unit: '%'
        },
        t5: {
          value: t5Value,
          unit: ''
        },
        load: {
          value: loadValue,
          unit: '%'
        }
      },
      variant: variant,
      locationName: product.locationName || null,
      wellSiteName: product.wellSiteName || null,
      isOnline: product.isOnline,
      status: variant,
      powerStatus: product.powerStatus,
      isActive: product.isActive
    };
  });

  return sortByPriority(processedProducts);
};

export const transformTurbineToGauge = turbine => {
  const turbineData = turbine?.info;
  const findValueInTable = key => {
    const summaryPath = getSummaryPath(turbineData) || [];
    const item = summaryPath.find(item => item.key === key);
    return item?.value || '0';
  };

  const findMaxInTable = key => {
    const summaryPath = getSummaryPath(turbineData) || [];
    const item = summaryPath.find(item => item.key === key);
    return item?.max || '0';
  };

  const safeParseFloat = val => {
    if (val === 'N' || val === null || val === undefined) return 0;
    const num = parseFloat(val);
    return isNaN(num) || !isFinite(num) ? 0 : num;
  };

  const genTotalKwAnalog = safeParseFloat(
    findValueInTable(TURBINES_KEYS.genTotalKwAnalog) ||
      turbine.info?.measures?.[TURBINES_KEYS.genTotalKwAnalog]?.value ||
      '0'
  );

  const maxTotalKW = safeParseFloat(
    findMaxInTable(TURBINES_KEYS.genTotalKwAnalog) ||
      turbine.info?.measures?.[TURBINES_KEYS.genTotalKwAnalog]?.max ||
      '0'
  );

  const ngp = safeParseFloat(
    findValueInTable(TURBINES_KEYS.ngpAnalog) ||
      turbine.info?.measures?.[TURBINES_KEYS.ngpAnalog]?.value ||
      '0'
  );

  const t5Temperature = safeParseFloat(
    findValueInTable(TURBINES_KEYS.t5AverageTemperatureAnalog) ||
      turbine.info?.measures?.[TURBINES_KEYS.t5AverageTemperatureAnalog]
        ?.value ||
      '0'
  );

  const pcntLoad = safeParseFloat(
    findValueInTable(TURBINES_KEYS.pcntLoadAnalog) ||
      turbine.info?.measures?.[TURBINES_KEYS.pcntLoadAnalog]?.value ||
      '0'
  );

  const variant = getTurbineVariant(turbine);

  return {
    id: turbine.id,
    title: turbine.id,
    subtitle: turbine.subType || 'Turbine',
    mainLabel: 'Gen Total kW',
    currentKw: genTotalKwAnalog,
    maxKw: maxTotalKW === 0 ? genTotalKwAnalog : maxTotalKW,
    metrics: {
      ngp: {
        value: ngp,
        unit: '%'
      },
      t5: {
        value: Math.round(t5Temperature)
      },
      load: {
        value: pcntLoad,
        unit: '%'
      }
    },
    variant: variant,
    locationName: turbine.locationName || null,
    wellSiteName: turbine.wellSiteName || null,
    isOnline: turbine.isOnline,
    status: turbine.status,
    powerStatus: turbine.powerStatus,
    isActive: turbine.isActive
  };
};

export const processTurbineData = turbinesData => {
  if (!Array.isArray(turbinesData)) return [];
  const transformedGauges = turbinesData.map(transformTurbineToGauge);
  return sortByPriority(transformedGauges);
};

export const transformSocketDataToGauge = (turbineData, turbineSocket) => {
  const safeParseFloat = val => {
    if (val === 'N' || val === null || val === undefined) return 0;
    const num = parseFloat(val);
    return isNaN(num) || !isFinite(num) ? 0 : num;
  };

  const genTotalKwAnalog = safeParseFloat(
    turbineSocket?.measures?.[TURBINES_KEYS.genTotalKwAnalog]?.toString() || '0'
  );
  const ngp = safeParseFloat(
    turbineSocket?.measures?.[TURBINES_KEYS.ngpAnalog]?.toString() || '0'
  );
  const t5Temperature = safeParseFloat(
    turbineSocket?.measures?.[
      TURBINES_KEYS.t5AverageTemperatureAnalog
    ]?.toString() || '0'
  );
  const pcntLoad = safeParseFloat(
    turbineSocket?.measures?.[TURBINES_KEYS.pcntLoadAnalog]?.toString() || '0'
  );

  const variant = getTurbineVariant(turbineSocket);
  return {
    ...turbineData,
    currentKw: genTotalKwAnalog,
    metrics: {
      ngp: {
        value: ngp,
        unit: '%'
      },
      t5: {
        value: Math.round(t5Temperature)
      },
      load: {
        value: pcntLoad,
        unit: '%'
      }
    },
    variant: variant,
    powerStatus: turbineSocket?.powerStatus,
    status: variant,
    isOnline: turbineSocket?.isOnline,
    isActive: turbineSocket?.isActive
  };
};

export const sortByPriority = gauges => {
  return [...gauges].sort((a, b) => {
    const statusOrder = {
      offline: 4,
      shutdown: 1,
      warning: 2,
      ok: 3
    };

    const statusA = statusOrder[a.variant] || 999;
    const statusB = statusOrder[b.variant] || 999;

    if (statusA !== statusB) {
      return statusA - statusB;
    }

    const titleA = a.title.toLowerCase();
    const titleB = b.title.toLowerCase();
    return titleA.localeCompare(titleB);
  });
};

export const filterGaugesBySearch = (gauges, searchQuery) => {
  if (!searchQuery.trim()) return gauges;

  const query = searchQuery.toLowerCase();
  return gauges.filter(gauge => {
    const turbineId = gauge.id.toLowerCase().includes(query);
    const turbineName = gauge.title.toLowerCase().includes(query);
    const model = gauge.subtitle.toLowerCase().includes(query);

    return turbineId || turbineName || model;
  });
};

export const mergeWithTurbineSocketData = ({ turbineData, turbineSocket }) => {
  if (
    !Array.isArray(turbineData) ||
    !Array.isArray(turbineSocket) ||
    turbineSocket.length === 0
  ) {
    return turbineData;
  }

  const turbineId = turbineSocket[0]?.id;
  if (!turbineId) {
    return turbineData;
  }

  const turbineIndex = turbineData.findIndex(
    turbine => turbine?.id === turbineId
  );

  if (turbineIndex === -1) {
    return turbineData;
  }

  const findTurbineData = turbineData[turbineIndex];
  const measures = turbineSocket?.[0]?.measures ?? {};
  const mergeTurbine = {
    ...findTurbineData,
    [TURBINES_KEYS.genTotalKwAnalog]: {
      ...findTurbineData?.[TURBINES_KEYS.genTotalKwAnalog],
      value: Number(measures?.[TURBINES_KEYS.genTotalKwAnalog])
    },
    measures: Object.fromEntries(
      Object.entries(measures ?? {}).map(([key, value]) => [key, Number(value)])
    )
  };

  const newTurbinesData = [...turbineData];
  newTurbinesData[turbineIndex] = mergeTurbine;

  return newTurbinesData;
};

export const mergeWithTurbineDetailsSocketData = ({
  turbineData,
  turbineSocket
}) => {
  const measures = turbineSocket?.measures ?? {};

  if (Array.isArray(turbineData)) {
    return turbineData?.map(item => {
      const socket = measures?.[item?.key];
      return socket
        ? {
            ...item,
            value: Number(socket?.value)
          }
        : item;
    });
  } else if (turbineData !== null && typeof turbineData === 'object') {
    const newTurbineData = Object.entries(turbineData).map(([key, array]) => {
      return {
        [key]: array.map(item => {
          const socket = measures?.[item?.key];
          return socket
            ? {
                ...item,
                value: Number(socket?.value)
              }
            : item;
        })
      };
    });

    return Object.assign({}, ...newTurbineData);
  }

  return turbineData;
};
